import { createContext, use, useEffect, useMemo, useState } from "react";
import type { JSX, ReactNode } from "react";
import type { AuthService, AuthUser } from "./AuthService";

/** Authentication status of the application. */
export type AuthStatus = "loading" | "signedIn" | "signedOut";

/** Value of the authentication context. */
export interface AuthContextValue {
  /** Current status. */
  readonly status: AuthStatus;
  /** Signed-in user or `null`. */
  readonly user: AuthUser | null;
  /** The authentication service. */
  readonly service: AuthService;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Properties of {@link AuthProvider}. */
export interface AuthProviderProps {
  /** The authentication service created in the composition root. */
  readonly service: AuthService;
  /** Child elements. */
  readonly children: ReactNode;
}

interface AuthState {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
}

/**
 * Loads the stored session and follows sign-in and sign-out.
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function AuthProvider(props: AuthProviderProps): JSX.Element {
  const { service } = props;
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });

  useEffect(() => {
    let active = true;
    const apply = (user: AuthUser | null): void => {
      if (active) {
        setState({ status: user === null ? "signedOut" : "signedIn", user });
      }
    };
    const unsubscribe = service.subscribe(apply);

    service.getUser().then(apply, () => {
      apply(null);
    });

    const cleanup = (): void => {
      active = false;
      unsubscribe();
    };

    return cleanup;
  }, [service]);

  const value = useMemo<AuthContextValue>(
    () => ({ status: state.status, user: state.user, service }),
    [state, service],
  );
  const provider = <AuthContext value={value}>{props.children}</AuthContext>;

  return provider;
}

/**
 * Returns the authentication context.
 *
 * @returns The context value.
 * @throws Error if no {@link AuthProvider} is present.
 */
export function useAuth(): AuthContextValue {
  const value = use(AuthContext);

  if (value === null) {
    throw new Error("useAuth must be used inside an AuthProvider.");
  }

  return value;
}
